import { useState, useCallback, useRef } from 'react';
import virtualTryOnService from '../services/virtualTryOnService';

/**
 * Custom hook for virtual try-on functionality
 * Provides state management and error handling for virtual try-on operations
 */
export const useVirtualTryOn = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [progress, setProgress] = useState(0);
  const abortControllerRef = useRef(null);

  /**
   * Try on a single garment
   */
  const tryOnGarment = useCallback(async (personImage, garmentImage, options = {}) => {
    try {
      setIsLoading(true);
      setError(null);
      setProgress(5);

      // Step 1: Initial validation
      setProgress(15);
      await new Promise(resolve => setTimeout(resolve, 200)); // Small delay for UX

      // Step 2: Check availability
      setProgress(25);
      const availability = await virtualTryOnService.checkAvailability(personImage);
      
      if (!availability.available) {
        throw new Error(availability.message);
      }

      // Step 3: Image optimization
      setProgress(45);
      await new Promise(resolve => setTimeout(resolve, 300)); // Simulate processing time

      // Step 4: Perform try-on
      setProgress(65);
      const tryOnResult = await virtualTryOnService.tryOnGarment(
        personImage, 
        garmentImage, 
        options
      );

      // Step 5: Finalize
      setProgress(90);
      await new Promise(resolve => setTimeout(resolve, 200));

      setProgress(100);
      setResult(tryOnResult);
      
      return tryOnResult;

    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
      setTimeout(() => setProgress(0), 500); // Keep progress visible briefly
    }
  }, []);

  /**
   * Try on multiple garments
   */
  const tryOnMultipleGarments = useCallback(async (personImage, garments, options = {}) => {
    try {
      setIsLoading(true);
      setError(null);
      setProgress(5);

      // Check availability first
      setProgress(15);
      const availability = await virtualTryOnService.checkAvailability(personImage);
      
      if (!availability.available) {
        throw new Error(availability.message);
      }

      // Process garments with progress updates
      const progressStep = 70 / garments.length; // Distribute 70% across garments
      let currentProgress = 25;

      setProgress(currentProgress);
      
      // Perform multi-garment try-on with progress tracking
      const tryOnResult = await virtualTryOnService.tryOnMultipleGarments(
        personImage, 
        garments, 
        {
          ...options,
          onProgress: (garmentIndex) => {
            currentProgress = 25 + (garmentIndex + 1) * progressStep;
            setProgress(currentProgress);
          }
        }
      );

      setProgress(95);
      await new Promise(resolve => setTimeout(resolve, 200));

      setProgress(100);
      setResult(tryOnResult);
      
      return tryOnResult;

    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
      setTimeout(() => setProgress(0), 500);
    }
  }, []);

  /**
   * Validate image quality
   */
  const validateImage = useCallback(async (image, imageType) => {
    try {
      setError(null);
      const validation = await virtualTryOnService.validateImage(image, imageType);
      return validation;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  /**
   * Check if virtual try-on is available
   */
  const checkAvailability = useCallback(async (personImage) => {
    try {
      setError(null);
      const availability = await virtualTryOnService.checkAvailability(personImage);
      return availability;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  /**
   * Get usage statistics
   */
  const getUsageStats = useCallback(async () => {
    try {
      setError(null);
      const stats = await virtualTryOnService.getUsageStats();
      return stats;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  /**
   * Cancel ongoing operation
   */
  const cancel = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setIsLoading(false);
    setProgress(0);
  }, []);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Clear result state
   */
  const clearResult = useCallback(() => {
    setResult(null);
  }, []);

  /**
   * Reset all state
   */
  const reset = useCallback(() => {
    setIsLoading(false);
    setError(null);
    setResult(null);
    setProgress(0);
  }, []);

  return {
    // State
    isLoading,
    error,
    result,
    progress,
    
    // Actions
    tryOnGarment,
    tryOnMultipleGarments,
    validateImage,
    checkAvailability,
    getUsageStats,
    cancel,
    clearError,
    clearResult,
    reset,
    
    // Service access
    service: virtualTryOnService
  };
};

export default useVirtualTryOn;